"use client";
import { Fragment, HTMLProps, useMemo } from "react";
import { useAppStore } from "@/store/app";
import { cn, getValue } from "@/utils/helpers";
import { ControlsStatGroupType } from "@/utils/types";
import { CONTROLS_DEFAULT } from "@/utils/constants";
import { getLang, getControlOptionsByType, getControlStatGroups, isYearOptionDisabled, getAgeGroups, isYearIndividual } from "@/utils/selectors";
import { SvgIconX } from "@/components/common/Svg";
import Button from "@/components/common/Button";
import Toggle from "@/components/common/Toggle";
import Select from "@/components/common/Select";
import Overlay from "@/components/common/Overlay";
import Search from "@/components/explorer/Search";
import SvgIconView from "@/components/common/Svg/components/SvgIconView";
import { PanelBackdrop } from "@/components/common/Panel";
import panelStyles from "@/components/common/Panel/Panel.module.css";
import ControlsStatsToggle from "./components/ControlsStatsToggle";
import styles from "./Controls.module.css";

const viewKeys = getControlOptionsByType("view");
const yearKeys = getControlOptionsByType("year");
const geoKeys = getControlOptionsByType("geo");
const statGroupKeys = getControlStatGroups();
const ageGroupKeys = getAgeGroups();

export interface ControlsProps extends HTMLProps<HTMLElement> {};

export default function Controls({
	className
} : ControlsProps) {
	const controls = useAppStore(store => store.controls);
	const setControl = useAppStore(store => store.setControl);
	const showMobileControls = useAppStore(store => store.showMobileControls);
	const setShowMobileControls = useAppStore(store => store.setShowMobileControls);
	
	const geoOptions = useMemo(() =>
		geoKeys.map((key: string) => ({
			value: key,
			label: getLang("geo", key),
		}))
	, []);

	const yearOptions = useMemo(() =>
		yearKeys.map((key: string) => ({
			value: String(key),
			label: getLang("year", key),
			small: isYearIndividual(key),
			disabled: isYearOptionDisabled(controls.view, controls.stat, key),
		}))
	, [controls]);

	const optionsByType = useMemo(() => ({
		geo: geoOptions,
		year: yearOptions,
	}), [geoOptions, yearOptions]);
	
	// const handleViewChange = (value: string) => {
		// handleChange("view", value);
		// router.replace(`${process.env.NEXT_PUBLIC_BASE_URL}/${value}`);
		// window.history.replaceState(null, "", `${process.env.NEXT_PUBLIC_BASE_URL}/${value}`)
	// }

	const handleChange = (type: string, value: string | number, suffix?: string) => {
		if(!value) return;
		if(suffix) return setControl(type, value);
		setControl(type, value);
	};

	const handleClose = () => {
		setShowMobileControls(false);
	};

	return (
		<>
			<div
				className={cn(
					styles.Controls,
					// styles[`Controls_${controls.view}`],
					// dataLoading ?  styles.Controls_loading : null,
					showMobileControls ? styles.Controls_mobileOpen : null,
					className
				)}
			>
				<Overlay
					className={cn(
						styles.ControlsOverlay,
						styles.ControlsOverlayPrimary
					)}
				>
					<div
						className={styles.ControlsInner}
					>
						<Toggle
							type="view"
							value={controls.view}
							options={viewKeys}
							IconComponent={SvgIconView}
							className={styles.ControlsViews}
							onToggleChange={value => handleChange("view", value)}
						/>
					</div>
					<Button
						square
						title="close data controls"
						onClick={handleClose}
						className={cn(
							panelStyles.PanelClose,
							styles.ControlsClose
						)}
					>
						<SvgIconX />
					</Button>
				</Overlay>
				<Overlay
					className={cn(
						styles.ControlsOverlay,
						styles.ControlsOverlaySecondary
					)}
				>
					<div
						className={styles.ControlsInner}
					>
						<div
							className={styles.ControlsOptions}
						>
							{statGroupKeys.map((group: ControlsStatGroupType, index: number) =>
								<Fragment
									key={index}
								>
									{group === "out" && controls.view !== "smoke" ? ageGroupKeys.map((age: string, index: number) =>
										<ControlsStatsToggle
											key={index}
											group={group}
											subGroup={age}
											onChange={value => handleChange("stat", value, age)}
										/>
									) : <ControlsStatsToggle
											key={index}
											group={group}
											onChange={value => handleChange("stat", value)}
										/>
									}
								</Fragment>
							)}
							{Object.keys(optionsByType).map(((type, index: number) =>
								<Select
									key={index}
									name={type}
									value={getValue(type, controls)}
									defaultValue={getValue(type, CONTROLS_DEFAULT)}
									options={getValue(type, optionsByType)}
									label={getLang(type)}
									onChange={value => handleChange(type, value)}
									className={cn(
										styles.ControlsSelect,
										styles[`ControlsSelect_${type}`]
									)}
									buttonProps={{
										className: styles.ControlsSelectTrigger
									}}
								/>
							))}
						</div>
						<Search
							className={styles.ControlsSearch}
						/>
					</div>
				</Overlay>
			</div>
			<PanelBackdrop
				show={showMobileControls}
				className={styles.ControlsBackdrop}
			/>
		</>
	);
}